import React, { useState } from "react";
import { Headphones } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import toast from "react-hot-toast";
import { api } from "../services/apiService"; // adjust path if needed

const Support = () => {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !message) {
      toast.error("Please fill all fields");
      return;
    }

    setLoading(true);
    try {
      // Optional backend integration
      await api.post("/support/submit", { email, message });
      toast.success("Support request sent successfully!");
      setEmail("");
      setMessage("");
    } catch (err) {
      console.error("Support form submission error:", err);
      toast.error("Failed to send request. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-4 md:px-6 py-6 bg-bg-secondary min-h-screen">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-2 mb-6">
          <Headphones className="w-7 h-7 text-primary-600" />
          <h1 className="text-2xl font-extrabold text-text-primary">
            Support Hub
          </h1>
        </div>

        <Separator className="mb-6" />

        {/* Form Card */}
        <Card className="p-6 md:p-8 text-center shadow-lg">
          <h2 className="text-xl font-extrabold mb-2 text-text-primary">
            Get In Touch
          </h2>
          <p className="text-sm mb-6 text-text-secondary">
            Submit your email and message, and our team will get back to you
            promptly.
          </p>

          <form
            onSubmit={handleSubmit}
            className="flex flex-col items-center gap-4 w-full max-w-md mx-auto"
          >
            <div className="w-full">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1"
              />
            </div>
            <div className="w-full">
              <Label htmlFor="message">Your Message</Label>
              <textarea
                id="message"
                rows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                className="mt-1 flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full max-w-[240px] mt-2"
            >
              {loading ? "Submitting..." : "Submit Request"}
            </Button>
          </form>
        </Card>

        {/* FAQ Section */}
        <div className="mt-8">
          <h3 className="text-lg font-extrabold text-center mb-4 text-primary-600">
            Frequently Asked Questions
          </h3>

          <Card className="shadow-md">
            <Accordion type="single" collapsible className="w-full">
              {[
                {
                  q: "How can I check my request status?",
                  a: "You'll receive an email confirmation with your ticket number. You can reply to that email for updates.",
                },
                {
                  q: "What is the typical response time?",
                  a: "Our support team responds within 24–48 hours on business days.",
                },
                {
                  q: "Can I attach files to my request?",
                  a: "Currently, attachments are supported via email replies after the ticket is created.",
                },
                {
                  q: "Is there a phone number for immediate assistance?",
                  a: "Yes, premium clients can contact us via their assigned support line provided in their account settings.",
                },
              ].map((item, idx) => (
                <AccordionItem key={idx} value={`item-${idx}`}>
                  <AccordionTrigger className="text-left font-semibold text-text-primary">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-text-secondary">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Support;
